import { useState } from "react";

interface SearchFilterProps {
  onSearch: (query: string) => void;
  onFilterPriority: (priority: string) => void;
  onFilterCategory: (category: string) => void;
  onSort: (sortBy: string) => void;
  totalTodos: number;
}

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" className="text-muted">
    <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10 10l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M1 3h12M3 7h8M5 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export default function SearchFilter({ onSearch, onFilterPriority, onFilterCategory, onSort, totalTodos }: SearchFilterProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [activePriority, setActivePriority] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [activeSort, setActiveSort] = useState("dueDate");

  const handleSearch = (val: string) => {
    setSearchQuery(val);
    onSearch(val);
  };

  const handlePriority = (val: string) => {
    const next = activePriority === val ? "" : val;
    setActivePriority(next);
    onFilterPriority(next);
  };

  const handleCategory = (val: string) => {
    const next = activeCategory === val ? "" : val;
    setActiveCategory(next);
    onFilterCategory(next);
  };

  const handleSort = (val: string) => {
    setActiveSort(val);
    onSort(val);
  };

  const priorities = [
    { value: "high",   label: "High",   color: "text-red-400 border-red-500/40 bg-red-500/10" },
    { value: "medium", label: "Med",    color: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
    { value: "low",    label: "Low",    color: "text-green-400 border-green-500/40 bg-green-500/10" },
  ];

  const sorts = [
    { value: "dueDate",  label: "Due date" },
    { value: "priority", label: "Priority" },
    { value: "newest",   label: "Newest" },
    { value: "oldest",   label: "Oldest" },
  ];

  const hasActiveFilters = activePriority || activeCategory;

  return (
    <div className="space-y-3">
      {/* Search + filter toggle */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2">
            <SearchIcon />
          </div>
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={e => handleSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-surface-alt border border-border-strong rounded-lg text-text placeholder-muted text-base font-medium focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/20 transition"
          />
          {searchQuery && (
            <button
              onClick={() => handleSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-text"
            >
              ✕
            </button>
          )}
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center justify-center sm:justify-start gap-2 px-3 py-2.5 rounded-lg text-base font-semibold border transition-all ${
            showFilters || hasActiveFilters
              ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
              : "bg-surface-alt text-muted border-border-strong hover:border-border-strong hover:text-text"
          }`}
        >
          <FilterIcon />
          Filters
          {hasActiveFilters && (
            <span className="w-4 h-4 rounded-full bg-amber-500 text-black text-[10px] font-bold flex items-center justify-center">
              {(activePriority ? 1 : 0) + (activeCategory ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {/* Filters dialog */}
      {showFilters && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4"
          onClick={e => { if (e.target === e.currentTarget) setShowFilters(false); }}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

          <div className="relative w-full max-w-lg max-h-[92vh] bg-surface border border-border-strong rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-border">
              <div>
                <h2 className="text-base font-bold text-text">Filters</h2>
                <p className="text-xs text-muted mt-0.5">Narrow down and sort your tasks</p>
              </div>
              <button
                onClick={() => setShowFilters(false)}
                className="p-2 rounded-lg text-muted hover:text-text hover:bg-surface-alt transition"
              >
                ✕
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto">
              {/* Priority pills */}
              <div>
                <p className="text-sm font-medium text-muted mb-2">Priority</p>
                <div className="flex flex-wrap gap-2">
                  {priorities.map(p => (
                    <button
                      key={p.value}
                      onClick={() => handlePriority(p.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-bold border transition-all ${
                        activePriority === p.value
                          ? p.color
                          : "text-muted bg-surface-alt border-border-strong hover:border-border-strong"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                  {activePriority && (
                    <button
                      onClick={() => handlePriority("")}
                      className="px-2 py-1.5 rounded-lg text-sm font-medium text-muted hover:text-text transition"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Category */}
              <div>
                <p className="text-sm font-medium text-muted mb-2">Category</p>
                <div className="flex flex-wrap gap-2">
                  {["work", "personal", "shopping", "health", "general"].map(cat => (
                    <button
                      key={cat}
                      onClick={() => handleCategory(cat)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all capitalize ${
                        activeCategory === cat
                          ? "bg-border-strong text-text border-border-strong"
                          : "text-muted bg-surface-alt border-border-strong hover:border-border-strong hover:text-text"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div>
                <p className="text-sm font-medium text-muted mb-2">Sort by</p>
                <div className="flex flex-wrap gap-2">
                  {sorts.map(s => (
                    <button
                      key={s.value}
                      onClick={() => handleSort(s.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all ${
                        activeSort === s.value
                          ? "bg-border-strong text-text border-border-strong"
                          : "text-muted bg-surface-alt border-border-strong hover:border-border-strong"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-4 sm:px-6 py-3 border-t border-border">
              <button
                onClick={() => setShowFilters(false)}
                className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
